<template>
  <div class="otp-by-stops-view">
    <div v-if="loading" class="text-center py-5">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Loading...</span>
      </div>
    </div>

    <div v-else>
      <!-- Route Header with Back Button -->
      <div class="stop-item">
        <div class="route-header mb-4 d-flex align-items-center">
          <button class="btn btn-sm btn-link-secondary me-3" @click="handleBack">
            <i class="fas fa-arrow-left me-2"></i>
            <!-- {{ $t('flexigo.otp.backToRoutes') }} -->
          </button>
          <div>
            <div class="fw-bold">
              {{ selectedRoute?.routeName }}
            </div>
            <a href="#" class="view-route-link" @click.prevent="handleViewRoute">
              {{ $t('flexigo.otp.viewRoute') }}
            </a>
          </div>
        </div>

        <!-- Legend and Export Controls -->
        <div class="d-flex justify-content-between align-items-center flex-wrap flex-row mb-5">
          <otp-legend :items="legendItems" :selected="selectedStatuses" @status-click="handleStatusFilter" />
          <div class="d-flex align-items-center gap-3">
            <export-graph :routes="visibleStops" :filename="`otp-stops-${selectedRoute?.routeName}`" />
          </div>
        </div>
      </div>

      <!-- Stops List -->
      <div class="stops-list">
        <div v-for="stop in visibleStops" :key="stop.id" class="stop-item">
          <div class="stop-name">
            {{ stop.name }}
          </div>
          <stacked-bar-chart :data="stop.stats" :stop-id="stop.id" :stop-name="stop.name" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import OtpLegend from '../charts/OtpLegend.vue';
import StackedBarChart from '../charts/StackedBarChart.vue';
import ExportGraph from '../components/ExportGraph.vue';

export default {
  name: 'OtpByStops',
  components: {
    OtpLegend,
    StackedBarChart,
    ExportGraph,
  },
  props: {
    filters: {
      type: Object,
      required: true,
    },
    selectedRouteId: {
      type: [String, Number],
      default: null,
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      selectedStatuses: ['late', 'onTime', 'early', 'skipped'],
      legendItems: [
        { label: 'Late', color: '#ff4059', key: 'late' },
        { label: 'On Time', color: '#687692', key: 'onTime' },
        { label: 'Early Departure', color: '#5287e3', key: 'early' },
        { label: 'Skipped', color: '#97a0b4', key: 'skipped' },
      ],
    };
  },
  computed: {
    ...mapGetters('otpDashboard', ['routeStats', 'getCombinedStopsForRoute']),

    selectedRoute() {
      if (!this.selectedRouteId || !this.routeStats) return null;
      return this.routeStats.find(
        (route) => route.routeId === this.selectedRouteId || route.routeName === this.selectedRouteId,
      );
    },

    stops() {
      if (!this.selectedRouteId) return [];
      return this.getCombinedStopsForRoute(this.selectedRouteId);
    },

    visibleStops() {
      // If all statuses are selected, show all stops
      if (this.selectedStatuses.length === 4) {
        return this.stops;
      }

      // Filter stops based on selected statuses
      return this.stops.filter((stop) => {
        const { stats } = stop;

        // Show stop if any selected status has a value > 0
        const hasSelectedStatus =
          (this.selectedStatuses.includes('late') && stats.late > 0) ||
          (this.selectedStatuses.includes('onTime') && stats.onTime > 0) ||
          (this.selectedStatuses.includes('early') && stats.early > 0) ||
          (this.selectedStatuses.includes('skipped') && stats.skipped > 0);

        // Also show stops with all zeros if all statuses are selected (edge case for non-categorized stops)
        const hasAnyValue = stats.late > 0 || stats.onTime > 0 || stats.early > 0 || stats.skipped > 0;

        return hasSelectedStatus || !hasAnyValue;
      });
    },
  },
  methods: {
    handleBack() {
      this.$emit('back');
    },
    handleViewRoute() {
      // Open route detail in new tab
      const routeDetailUrl = `${window.location.origin}/admin/flexigo/#/routeDetail/${this.selectedRouteId}`;
      window.open(routeDetailUrl, '_blank');
    },
    handleStatusFilter(status) {
      const index = this.selectedStatuses.indexOf(status);
      if (index > -1) {
        this.selectedStatuses.splice(index, 1);
      } else {
        this.selectedStatuses.push(status);
      }
    },
  },
};
</script>

<style scoped>
.otp-by-stops-view {
  padding: 20px;
  position: relative;
}

.route-header {
  display: flex;
  align-items: center;
}

.view-route-link {
  color: #5667ff;
  text-decoration: none;
  font-size: 0.875rem;
}

.view-route-link:hover {
  text-decoration: underline;
}

.stop-item {
  display: grid;
  grid-template-columns: 2fr 5fr;
  gap: 1rem;
  align-items: center;
  margin-bottom: 1em;
  background: #fff;
}

.stop-name {
  font-size: 0.9rem;
  font-weight: 500;
  text-align: right;
  color: #000b20;
}
</style>
